import React, { useState, useContext, useEffect } from 'react';
import { callApi } from '../callApi';
import { AppContext } from '../components/AppContext';
import {
  Box,
  Grid,
  GridItem,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Divider,
  Alert,
  AlertIcon,
  Flex,
  Spinner,
  Icon,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Select
} from "@chakra-ui/react";
import { RepeatIcon, CheckCircleIcon, WarningIcon, WarningTwoIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  ResponsiveContainer, Legend 
} from 'recharts';

function ClientAdmin() {
  const [users, setUsers] = useState([]);
  const [claimableUsers, setClaimableUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingClaimable, setIsLoadingClaimable] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isClaimingUser, setIsClaimingUser] = useState(null); // Store ID of user being claimed
  const [isChangingRole, setIsChangingRole] = useState(null); // Store ID of user whose role is changing
  const [hasPermission, setHasPermission] = useState(true);
  const [canChangeRoles, setCanChangeRoles] = useState(false);
  const [processingMetrics, setProcessingMetrics] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('loading'); // 'healthy', 'warning', 'critical', 'loading'
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  
  // Create user form states
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [userContext, setUserContext] = useState('');
  
  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const response = await callApi('/get_user_list', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.status === 403) {
        setHasPermission(false);
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      if (data && data.user_list && Array.isArray(data.user_list)) {
        setUsers(data.user_list);
        setCanChangeRoles(data.can_change_roles || false);
        setHasPermission(true);
      } else {
        setUsers([]);
        console.warn('Received invalid data from API:', data);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadClaimableUsers = async () => {
    setIsLoadingClaimable(true);
    try {
      const response = await callApi('/claimable_users', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.status === 403) {
        // User might not have the switch_user_org permission, which is fine.
        setClaimableUsers([]);
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch claimable users');
      }
      
      const data = await response.json();
      if (data && data.claimable_users && Array.isArray(data.claimable_users)) {
        setClaimableUsers(data.claimable_users);
      } else {
        setClaimableUsers([]);
      }
    } catch (err) {
      console.error('Error loading claimable users:', err);
      setClaimableUsers([]);
    } finally {
      setIsLoadingClaimable(false);
    }
  };
  
  const loadProcessingMetrics = async () => {
    setIsLoadingMetrics(true);
    try {
      const response = await callApi('/processing_metrics', accessToken, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch processing metrics');
      }
      
      const data = await response.json();
      setProcessingMetrics(data.metrics);
      
      // Calculate processing health status
      calculateProcessingHealth(data.metrics);
    } catch (err) {
      console.error('Error loading processing metrics:', err);
      setProcessingStatus('critical');
      toast({
        title: 'Error loading metrics',
        description: 'Could not retrieve processing metrics.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingMetrics(false);
    }
  };
  
  const calculateProcessingHealth = (metrics) => {
    if (!metrics || metrics.length === 0) {
      setProcessingStatus('critical');
      return;
    }
    
    // Get the most recent day's data
    const today = metrics[metrics.length - 1];
    
    // Simple rules for health status:
    // 1. Critical: No successful processing in the last day
    // 2. Warning: Less than 50% of new cases were successfully processed
    // 3. Healthy: Otherwise
    
    if (today.success === 0) {
      setProcessingStatus('critical');
    } else if (today.new_cases > 0 && today.success / today.new_cases < 0.5) {
      setProcessingStatus('warning');
    } else {
      setProcessingStatus('healthy');
    }
  };
  
  const handleCreateUser = async (event) => {
    event.preventDefault();
    setIsCreatingUser(true);

    // Basic validation
    if (!email || !name) {
      toast({
        title: 'Missing information',
        description: 'Please provide both email and name.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      setIsCreatingUser(false);
      return;
    }

    try {
      const response = await callApi('/create_user', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email, 
          name: name,
          user_context: userContext,  
        }),
      });
      
      if (response.status === 403) {
        toast({
          title: 'Permission Denied',
          description: 'You do not have permission to create users.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setHasPermission(false);
        setIsCreatingUser(false);
        return;
      }
      
      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'User created',
          description: `${name} (${email}) has been added successfully.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setEmail('');
        setName('');
        setUserContext('');
        loadUsers(); // Refresh the user list
      } else {
        toast({
          title: 'Creation failed',
          description: data.error || 'Failed to create user.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleChangeRole = async (targetUserId, newRole) => {
    setIsChangingRole(targetUserId);
    try {
      const response = await callApi('/change_user_role', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          target_user_id: targetUserId,
          role: newRole
        }),
      });
      
      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'Role Updated',
          description: data.message || 'User role successfully updated.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        loadUsers(); // Refresh the main user list to show new roles
      } else {
        toast({
          title: 'Update failed',
          description: data.error || 'Failed to update user role.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred while updating the role.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsChangingRole(null);
    }
  };

  const handleClaimUser = async (targetUserId) => {
    setIsClaimingUser(targetUserId);
    try {
      const response = await callApi('/claim_user', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ target_user_id: targetUserId }),
      });
      
      const data = await response.json();

      if (response.ok) {
        toast({
          title: 'User Claimed',
          description: data.message || 'User successfully added to your organization.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        loadUsers(); // Refresh the main user list
        loadClaimableUsers(); // Refresh the claimable list
      } else {
        toast({
          title: 'Claim failed',
          description: data.error || 'Failed to claim user.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred while claiming the user.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsClaimingUser(null);
    }
  };
  
  // Sort users by email if we have users
  const sortedUsers = users.length > 0 ? [...users].sort((a, b) => a.email.localeCompare(b.email)) : [];
  const sortedClaimableUsers = claimableUsers.length > 0 ? [...claimableUsers].sort((a, b) => a.email.localeCompare(b.email)) : [];
  
  // Load users and metrics on component mount
  useEffect(() => {
    loadUsers();
    loadClaimableUsers();
    loadProcessingMetrics();
  }, []);

  // Processing Health Dashboard Component
  const ProcessingHealthDashboard = () => {
    if (!processingMetrics) {
      return (
        <Flex justify="center" my={4}>
          <Spinner size="lg" />
        </Flex>
      );
    }
    
    // Get the last 7 days of data for the summary
    const recentData = processingMetrics.slice(-7);
    
    // Calculate totals and averages
    const totalNewCases = recentData.reduce((sum, day) => sum + day.new_cases, 0);
    const totalSuccess = recentData.reduce((sum, day) => sum + (day.success || 0), 0);
    const successRate = totalNewCases > 0 ? (totalSuccess / totalNewCases * 100).toFixed(1) : 0;
    
    // Get yesterday's data for the main display
    const yesterday = processingMetrics[processingMetrics.length - 1];
    
    // Status configuration
    const statusConfig = {
      healthy: { 
        color: 'green.500', 
        bgColor: 'green.50', 
        icon: CheckCircleIcon, 
        text: 'Healthy',
        description: 'Processing is operating normally'
      },
      warning: { 
        color: 'orange.500', 
        bgColor: 'orange.50', 
        icon: WarningIcon, 
        text: 'Warning',
        description: 'Processing volume is lower than expected'
      },
      critical: { 
        color: 'red.500', 
        bgColor: 'red.50', 
        icon: WarningTwoIcon, 
        text: 'Critical',
        description: 'Processing may be stopped or failing'
      },
      loading: { 
        color: 'blue.500', 
        bgColor: 'blue.50', 
        icon: null, 
        text: 'Loading...',
        description: 'Retrieving processing status'
      }
    };
    
    const currentStatus = statusConfig[processingStatus];
    
    return (
      <Box bg="white" p={5} borderRadius="md" boxShadow="sm" mb={6}>
        <Flex justify="space-between" align="center" mb={4}>
          <Heading as="h2" size="md" textAlign="left">
            Processing Health Status
          </Heading>
          <Button 
            leftIcon={<RepeatIcon />}
            onClick={loadProcessingMetrics}
            isLoading={isLoadingMetrics}
            size="sm"
          >
            Refresh
          </Button>
        </Flex>
        
        {/* Status Alert */}
        <Alert 
          status={processingStatus === 'healthy' ? 'success' : processingStatus === 'warning' ? 'warning' : 'error'}
          variant="subtle"
          borderRadius="md"
          mb={4}
        >
          <AlertIcon />
          <Flex justify="space-between" width="100%" align="center">
            <Box>
              <Text fontWeight="bold">{currentStatus.text}</Text>
              <Text fontSize="sm">{currentStatus.description}</Text>
            </Box>
            <Badge 
              colorScheme={processingStatus === 'healthy' ? 'green' : processingStatus === 'warning' ? 'orange' : 'red'}
              fontSize="0.8em"
              p={1}
            >
              Last updated: {new Date().toLocaleTimeString()}
            </Badge>
          </Flex>
        </Alert>
        
        {/* Key Metrics */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={6}>
          <Stat bg="gray.50" p={3} borderRadius="md" boxShadow="sm">
            <StatLabel>Yesterday's Cases</StatLabel>
            <StatNumber>{yesterday.new_cases}</StatNumber>
            <StatHelpText>New cases received</StatHelpText>
          </Stat>
          
          <Stat bg="gray.50" p={3} borderRadius="md" boxShadow="sm">
            <StatLabel>Successfully Processed</StatLabel>
            <StatNumber>{yesterday.success || 0}</StatNumber>
            <StatHelpText>Completed successfully</StatHelpText>
          </Stat>
          
          <Stat bg="gray.50" p={3} borderRadius="md" boxShadow="sm">
            <StatLabel>Exceptions</StatLabel>
            <StatNumber>{yesterday.exceptions || 0}</StatNumber>
            <StatHelpText>Requires attention</StatHelpText>
          </Stat>
          
          <Stat bg="gray.50" p={3} borderRadius="md" boxShadow="sm">
            <StatLabel>Success Rate</StatLabel>
            <StatNumber>{successRate}%</StatNumber>
            <StatHelpText>Last 7 days</StatHelpText>
          </Stat>
        </SimpleGrid>
        
        {/* Processing Trend Chart */}
        <Box height="250px" mb={4}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={processingMetrics}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tickFormatter={(date) => {
                  const d = new Date(date);
                  return `${d.getMonth()+1}/${d.getDate()}`;
                }}
              />
              <YAxis />
              <RechartsTooltip 
                formatter={(value, name) => [value, name]}
                labelFormatter={(date) => new Date(date).toLocaleDateString()}
              />
              <Legend />
              <Line type="monotone" dataKey="new_cases" stroke="#3182CE" name="New Cases" />
              <Line type="monotone" dataKey="success" stroke="#38A169" name="Success" />
              <Line type="monotone" dataKey="exceptions" stroke="#DD6B20" name="Exceptions" />
              <Line type="monotone" dataKey="processed" stroke="#805AD5" name="Processed" />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    );
  };

  return (
    <Box textAlign="center" fontSize="xl">
      <Grid
        templateAreas={`"header header"    
                        "main main"
                        "footer footer"`}
        gridTemplateRows={'60px auto 1fr 60px'}
        gridTemplateColumns={'1fr 1fr'}
        h='100%'
        gap='2'
        color='blackAlpha.800'
        fontSize='12px'
      >
        <HeaderMenus />

        <GridItem bg='gray.50' padding={6} area={'main'}>
          <Box maxWidth="1200px" margin="0 auto">
            <Heading as="h1" size="lg" mb={6} textAlign="left">
              Organization Management
            </Heading>
            
            
            <Divider my={6} />
            
            <Heading as="h2" size="md" mb={6} textAlign="left">
              User Management
            </Heading>
            
            {!hasPermission ? (
              <Alert status="warning" borderRadius="md" mb={6}>
                <AlertIcon />
                <Text>You don't have permission to manage users. Please contact your administrator.</Text>
              </Alert>
            ) : (
              <>
                {/* Create User Form */}
                <Box bg="white" p={5} borderRadius="md" boxShadow="sm" mb={8}>
                  <Heading as="h2" size="md" mb={4} textAlign="left">
                    Add New User
                  </Heading>
                  
                  <form onSubmit={handleCreateUser}>
                    <Flex gap={4} flexWrap="wrap">
                      <FormControl id="email" isRequired flex="1" minW="250px">
                        <FormLabel>Email address</FormLabel>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="user@example.com"
                        />
                      </FormControl>
                      
                      <FormControl id="name" isRequired flex="1" minW="250px">
                        <FormLabel>Name</FormLabel>
                        <Input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Full Name"
                        />
                      </FormControl>
                    </Flex>

                    <FormControl id="user_context" mt={4}>
                      <FormLabel>User Description (Optional)</FormLabel>
                      <Input
                        type="text"
                        value={userContext}
                        onChange={(e) => setUserContext(e.target.value)}
                        placeholder="Brief description of user's role or responsibilities"
                      />
                    </FormControl>

                    <Button
                      mt={4}
                      colorScheme="blue"
                      type="submit"
                      isLoading={isCreatingUser}
                    >
                      Create User
                    </Button>
                  </form>
                </Box>


                <Divider my={6} />
              </>
            )}
            
            {/* User List */}
            <Flex justify="space-between" align="center" mb={4}>
              <Heading as="h2" size="md" textAlign="left">
                Organization Users
              </Heading>
              <Button 
                leftIcon={<RepeatIcon />}
                onClick={loadUsers}
                isLoading={isLoading}
                size="sm"
              >
                Refresh
              </Button>
            </Flex>
            
            {isLoading ? (
              <Flex justify="center" my={8}>
                <Spinner size="xl" />
              </Flex>
            ) : !hasPermission ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text>User information is not available. You may not have sufficient permissions.</Text>
              </Alert>
            ) : sortedUsers.length > 0 ? (
              <Box overflowX="auto" bg="white" borderRadius="md" boxShadow="sm">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Email</Th>
                      <Th>Name</Th>
                      <Th>Current Role</Th>
                      <Th>Organization</Th>
                      {canChangeRoles && <Th>Change Role</Th>}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedUsers.map((user) => (
                      <Tr key={user.id || user.email}>
                        <Td>{user.email}</Td>
                        <Td>{user.name || 'N/A'}</Td>
                        <Td>{user.role || 'user'}</Td>
                        <Td>{user.org_name || 'N/A'}</Td>
                        {canChangeRoles && (
                          <Td>
                            <Select 
                              size="sm" 
                              value={user.role || 'user'} 
                              onChange={(e) => handleChangeRole(user.id, e.target.value === 'user' ? null : e.target.value)}
                              isDisabled={isChangingRole === user.id}
                              bg={isChangingRole === user.id ? "gray.100" : "white"}
                            >
                              <option value="user">User</option>
                              <option value="client_admin">Client Admin</option>
                              <option value="disabled">Disabled</option>
                            </Select>
                          </Td>
                        )}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            ) : (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text>No users found. {hasPermission ? 'Create a new user to get started.' : ''}</Text>
              </Alert>
            )}

            {/* Claimable Users Section */}
            {sortedClaimableUsers.length > 0 && (
              <>
                <Divider my={6} />
                <Flex justify="space-between" align="center" mb={4}>
                  <Heading as="h2" size="md" textAlign="left">
                    Unclaimed Users (Matching Domain)
                  </Heading>
                  <Button 
                    leftIcon={<RepeatIcon />}
                    onClick={loadClaimableUsers}
                    isLoading={isLoadingClaimable}
                    size="sm"
                  >
                    Refresh
                  </Button>
                </Flex>
                
                <Alert status="info" borderRadius="md" mb={4}>
                  <AlertIcon />
                  <Text>These users registered with an email domain belonging to your organization. You can claim them to manage their access.</Text>
                </Alert>

                <Box overflowX="auto" bg="white" borderRadius="md" boxShadow="sm" mb={8}>
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Email</Th>
                        <Th>Name</Th>
                        <Th>Current Role</Th>
                        <Th>Current Organization</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {sortedClaimableUsers.map((user) => (
                        <Tr key={user.id}>
                          <Td>{user.email}</Td>
                          <Td>{user.name || 'N/A'}</Td>
                          <Td>{user.role || 'user'}</Td>
                          <Td>{user.current_org_name}</Td>
                          <Td>
                            <Button
                              size="sm"
                              colorScheme="green"
                              onClick={() => handleClaimUser(user.id)}
                              isLoading={isClaimingUser === user.id}
                              loadingText="Claiming..."
                            >
                              Claim User
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </>
            )}
          </Box>
            {/* Processing Health Dashboard */}
            <ProcessingHealthDashboard />

        </GridItem>
      </Grid>
    </Box>
  );
}

export default ClientAdmin;